import React, { memo, useCallback, useMemo, useState } from 'react';
import { FlyoutContainer, HeaderActionLink, MenuItem } from '@cmhc-schl/hearth-ui';
import { Module, ModuleMapping, UserRole } from '@hobt/constants';
import { useMediaQuery, useTheme } from '@mui/material';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { isUserInRoles } from 'Components/Common/UserHelpers/CheckUserRole';
import { useAuthenticationContext } from 'Foundation/Authentication';
import { HbtSitecoreContextType } from 'Foundation/HydrateSitecoreContext';
import i18n from 'i18next';
import Menu from './MenuV5Adapter';
import { HbtHeaderSideMenuProps } from './types';
import { FeatureFlags } from '../../../../Enums/FeatureFlag.enum';
import { useFeature } from 'flagged';
import { useCacheApplicationConfig } from '../Utils/UseCacheApplicationConfig';

const HbtHeaderSideMenu: React.FC<HbtHeaderSideMenuProps> = ({
  text,
  sideMenuItems,
  logoutLabel
}) => {
  const theme = useTheme();
  const isMediumUp = useMediaQuery(theme.breakpoints.up('md'));
  const authenticationContext = useAuthenticationContext();

  const { sitecoreContext } = useSitecoreContext() as unknown as {
    sitecoreContext: HbtSitecoreContextType;
  };
  const isInternalSite = useFeature(FeatureFlags.INTERNAL) as boolean;

  const moduleMapDefault = useMemo(
    () => ({
      piFileShare: [ModuleMapping.piFileShare],
      piPurposeTest: [ModuleMapping.piPurposeTest],
      piRequest: [ModuleMapping.piRequest],
      user: [ModuleMapping.admin],
      arrears: [ModuleMapping.arrears],
      default: [ModuleMapping.default],
      claims: [ModuleMapping.claim],
      financialApproval: [ModuleMapping.claim, ModuleMapping.arrears, ModuleMapping.default],
      userAccessManagement: [ModuleMapping.admin],
      help: [ModuleMapping.claim, ModuleMapping.arrears, ModuleMapping.default],
      piProcessing: [ModuleMapping.piProcessing]
    }),
    []
  );

  const cachedConfig = useCacheApplicationConfig();
  const cachedGlobalMenuAccesses = cachedConfig.globalMenuAccesses;

  const globalMenuAccessesDefault: Record<string, boolean> = {
    portfolioInsurance:
      sitecoreContext.isPortfolioSite !== undefined
        ? !!sitecoreContext.isPortfolioSite
        : !!cachedGlobalMenuAccesses?.portfolioInsurance,

    piProcessing:
      sitecoreContext.isPortfolioSite !== undefined
        ? !!sitecoreContext.isPortfolioSite
        : !!cachedGlobalMenuAccesses?.piProcessing,

    arrears:
      sitecoreContext.isServicingSite !== undefined
        ? !!sitecoreContext.isServicingSite
        : !!cachedGlobalMenuAccesses?.arrears,

    default:
      sitecoreContext.isServicingSite !== undefined
        ? !!sitecoreContext.isServicingSite
        : !!cachedGlobalMenuAccesses?.default,

    claims:
      sitecoreContext.isServicingSite !== undefined
        ? !!sitecoreContext.isServicingSite
        : !!cachedGlobalMenuAccesses?.claims,

    financialApproval:
      sitecoreContext.isServicingSite !== undefined
        ? !!(sitecoreContext.isServicingSite && isInternalSite)
        : !!cachedGlobalMenuAccesses?.financialApproval,

    userAccessManagement: sitecoreContext.user?.modules
      ? !!(sitecoreContext.user.modules.includes(Module.Admin) && !sitecoreContext.isPortfolioSite)
      : !!cachedGlobalMenuAccesses?.userAccessManagement,

    help:
      sitecoreContext.isServicingSite !== undefined
        ? !!sitecoreContext.isServicingSite
        : !!cachedGlobalMenuAccesses?.help,

    support:
      sitecoreContext.isPortfolioSite !== undefined
        ? !!sitecoreContext.isPortfolioSite
        : !!cachedGlobalMenuAccesses?.support
  };
  const { moduleMap, globalMenuAccesses, user } = useCacheApplicationConfig(
    moduleMapDefault,
    globalMenuAccessesDefault,
    sitecoreContext.user
  );

  const [isActive, setIsActive] = useState(false);
  const sideMenuClickHandler = useCallback((e?: React.MouseEvent | React.KeyboardEvent) => {
    if (e) e.stopPropagation();
    setIsActive((prev) => !prev);
  }, []);

  const userModuleAccess = useMemo(
    () =>
      user?.moduleRoleMapping
        ? Object.keys(user.moduleRoleMapping).map((k) => parseInt(k, 10))
        : [],
    [user]
  );

  const isMasterUser = useMemo(() => {
    if (!user?.moduleRoleMapping) return false;
    const roles = Object.values(user.moduleRoleMapping).flat();
    return roles.includes(UserRole.MasterUser);
  }, [user]);

  const filterParentMenuItems = useCallback(
    (menuItem: MenuItem): boolean => {
      const parentItemName = menuItem.fields.name?.value;
      const navItem = menuItem.fields.navItem;

      if (navItem?.value?.href && !navItem.value.href.includes(i18n.language)) {
        menuItem = {
          ...menuItem,
          fields: {
            ...menuItem.fields,
            navItem: {
              ...navItem,
              value: { ...navItem.value, href: `/${i18n.language}${navItem.value.href}` }
            }
          }
        };
      }

      return !!(parentItemName && globalMenuAccesses?.[parentItemName]);
    },
    [globalMenuAccesses]
  );

  const filterSubMenuItems = useCallback(
    (menuItems: MenuItem[]): MenuItem[] => {
      if (!userModuleAccess.length) return [];

      const hasPermission = (menuItem: MenuItem): boolean => {
        const name = menuItem.fields.name?.value;
        if (!name) return false;

        if (['Search', 'Recherche'].includes(menuItem.fields.text?.value ?? '')) return false;

        const moduleIds = moduleMap?.[name];
        if (!moduleIds) return false;

        if (!userModuleAccess.some((id) => moduleIds.includes(id))) return false;

        const navItemName = menuItem.fields.navItem?.value?.name;
        if (navItemName === 'delegateAuthority' && (!isInternalSite || !isMasterUser)) return false;

        if (moduleIds.includes(Module.Admin) && user) {
          const roles = user.moduleRoleMapping;
          if (
            !isUserInRoles(Module.Admin, [UserRole.CmhcAdministrator], roles) &&
            !isUserInRoles(Module.Admin, [UserRole.LenderAdministrator], roles)
          ) {
            return false;
          }
        }

        return true;
      };

      return menuItems
        .filter((menuItem) => {
          const flag = menuItem.fields.navItem?.value?.featureFlag;
          if (!flag) return true;
          const [flagName, state] = flag.split('=');
          return useFeature(flagName) === (state === 'ON');
        })
        .map((menuItem) => {
          const href = menuItem.fields.navItem?.value?.href;
          const menuItemWithLang =
            href && !href.includes(i18n.language)
              ? {
                  ...menuItem,
                  fields: {
                    ...menuItem.fields,
                    navItem: {
                      ...menuItem.fields.navItem!,
                      value: { ...menuItem.fields.navItem!.value, href: `/${i18n.language}${href}` }
                    }
                  }
                }
              : menuItem;

          const filteredChildren = menuItemWithLang.fields.subMenu
            ? filterSubMenuItems(menuItemWithLang.fields.subMenu)
            : [];
          return {
            ...menuItemWithLang,
            fields: { ...menuItemWithLang.fields, subMenu: filteredChildren }
          };
        })
        .filter((item) => hasPermission(item) || (item.fields.subMenu?.length ?? 0) > 0);
    },
    [userModuleAccess, user, moduleMap, i18n.language, isInternalSite, isMasterUser]
  );

  const processedSubMenuItems = useCallback(
    (menuItem: MenuItem): MenuItem => ({
      ...menuItem,
      fields: { ...menuItem.fields, subMenu: filterSubMenuItems(menuItem.fields.subMenu ?? []) }
    }),
    [filterSubMenuItems]
  );

  const processedSideMenuItems = useMemo(
    () => (items: MenuItem[]) => items.filter(filterParentMenuItems).map(processedSubMenuItems),
    [filterParentMenuItems, processedSubMenuItems]
  );

  return (
    <>
      <HeaderActionLink
        iconType="icon_menu"
        isMediumUp={isMediumUp}
        text={text?.value ?? 'Menu'}
        onToggle={sideMenuClickHandler}
      />

      <FlyoutContainer
        id="header-menu-flyout-container"
        isHeaderRequired={false}
        onClose={() => {
          setIsActive((prev) => !prev);
        }}
        open={isActive}
        content={
          <Menu.InFlyout
            hasBackIcon={false}
            hasChevron={true}
            headerText={text}
            isLogoutOptionRequired={authenticationContext.isAuthenticated}
            logoutLabel={logoutLabel}
            items={processedSideMenuItems(sideMenuItems)}
            onClickClose={sideMenuClickHandler}
            onNavLinkClick={sideMenuClickHandler}
            onClickLogout={authenticationContext.signOut}
          />
        }
      />
    </>
  );
};

export default memo(HbtHeaderSideMenu);
